/**
 * DetailModal — connector detail (520px). Header avatar+name, purpose,
 * expired warning, labels, product chips, change/version/execs row, and a
 * footer whose actions depend on role + connection state.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';
import { Button, LabelChip, Modal } from '../components';
import { Chart, Close, Cube, Gear, Pencil, WarningCircle } from '../icons';
import { useBridge } from '../store/bridgeStore';
import { allInts } from '../domain/catalog';
import { versionsFor } from '../domain/versions';

export default function DetailModal() {
  const { state, actions } = useBridge();
  if (!state.detailId) return null;
  const ints = allInts(state.adapters, state.metaOverrides);
  const d = ints.find(i => i.id === state.detailId);
  if (!d) return null;

  const c = state.conn[d.id] || { connected: false, status: null, vendorVersion: null };
  const isActive = c.connected && c.status === 'active';
  const isExpired = c.connected && c.status === 'expired';
  const available = !c.connected;
  const isSoftknack = state.role === 'softknack';
  const canEdit = isSoftknack && d.cls !== 'mcp';
  const canManageMcp = isSoftknack && d.cls === 'mcp';

  const vs = versionsFor(d.id, state.versions, state.adapters);
  const liveV = vs.find(x => x.status === 'published') || vs[0] || ({} as any);
  const versionFull = 'v1 × ' + (liveV.vendor || c.vendorVersion || '1.0') + ' · rev ' + (liveV.rev || 1);
  const execsLabel = (liveV.execs || 0).toLocaleString() + ' runs';

  const labels = [
    { group: 'Product type', value: d.category || 'Custom' },
    { group: 'Company', value: d.company || d.name },
    { group: 'Adapter', value: d.cls.toUpperCase() + ' adapter' },
    { group: 'Change delivery', value: d.change || 'poll' },
  ];

  return (
    <Modal width={520} zIndex={60} onClose={actions.closeDetail}>
      {/* header */}
      <View style={styles.header}>
        <View style={[styles.avatar, { backgroundColor: d.color } as any]}>
          <Text style={styles.avatarText}>{d.initials}</Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.name}>{d.name}</Text>
          <Text style={styles.cls}>{d.cls.toUpperCase()} adapter</Text>
        </View>
        <Pressable onPress={actions.closeDetail}><Close size={18} color={colors.ink3} /></Pressable>
      </View>

      <ScrollView style={{ maxHeight: '60vh' as any }} contentContainerStyle={{ padding: 20 }}>
        <Text style={styles.purpose}>{d.purpose}</Text>

        {isExpired && (
          <View style={styles.warnBox}>
            <WarningCircle size={16} color={colors.warn} />
            <View style={{ flex: 1 }}>
              <Text style={styles.warnTitle}>Connection needs re-authorizing</Text>
              <Text style={styles.warnSub}>Your access expired. Reconnect to keep this integration working.</Text>
            </View>
          </View>
        )}

        <Text style={styles.sectionLabel}>Labels</Text>
        <View style={styles.chipWrap}>
          {labels.map((l, i) => <LabelChip key={i} group={l.group} value={l.value} size={26} valueColor={colors.ink} />)}
        </View>

        <Text style={styles.sectionLabel}>Can serve these SK products</Text>
        <View style={styles.chipWrap}>
          {d.products.map((p, i) => (
            <View key={i} style={styles.productChip}>
              <Text style={styles.productChipText}>{p.key + ' · ' + p.dataType}</Text>
            </View>
          ))}
        </View>

        <View style={styles.metaRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.metaKey}>Change delivery</Text>
            <Text style={styles.metaVal}>{d.change}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.metaKey}>Live version</Text>
            <Text style={styles.metaVal}>{c.connected ? versionFull : '— not connected —'}</Text>
          </View>
          {c.connected && (
            <View style={{ flex: 1 }}>
              <Text style={styles.metaKey}>Executions</Text>
              <Text style={styles.metaVal}>{execsLabel}</Text>
            </View>
          )}
        </View>
      </ScrollView>

      {/* footer */}
      <View style={styles.footer}>
        <View style={styles.footerLeft}>
          {canEdit && (
            <>
              <Button label="Edit details" variant="secondary" onPress={() => actions.openMetaManage(d.id)}
                left={<Gear size={13} color={colors.ink3} />} right={<Internal />} />
              <Button label="Edit mapping in Builder" variant="secondary" onPress={() => actions.editInBuilder(d.id)}
                left={<Pencil size={13} color={colors.accent} />} right={<Internal />} />
            </>
          )}
          {canManageMcp && (
            <Button label="Manage MCP" variant="secondary" onPress={() => actions.openMcpManage(d.id)}
              left={<Cube size={13} color={colors.success} />} right={<Internal />} />
          )}
        </View>
        <View style={styles.footerRight}>
          {c.connected && (
            <Button label="Activity & logs" variant="secondary" onPress={() => actions.openLogs(d.id)} left={<Chart size={14} color={colors.ink2} />} />
          )}
          <Button label="Close" variant="ghost" onPress={actions.closeDetail} />
          {available && <Button label="Connect" onPress={() => actions.openConnect(d.id)} height={36} />}
          {/* True disconnect = revoke the connection (POST /connections/:id/revoke).
              Distinct from Manage-MCP's "Remove MCP config", which only drops the
              server wiring. revoke() flips the pill to not-connected and tears down
              the grant routes — the action existed but had no UI entry point. */}
          {isActive && <Button label="Disconnect" variant="warn" onPress={() => actions.revoke(d.id)} height={36} />}
          {isActive && <Button label="Manage grants" onPress={() => actions.openGrant(d.id)} height={36} />}
          {isExpired && <Button label="Re-authorize" variant="warn" onPress={() => actions.reauth(d.id)} height={36} />}
        </View>
      </View>
    </Modal>
  );
}

function Internal() {
  return (
    <View style={styles.internalBadge}><Text style={styles.internalText}>internal</Text></View>
  );
}

const styles = StyleSheet.create({
  header: { padding: 18, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 14 },
  avatar: { width: 46, height: 46, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: '#fff' },
  name: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.xl, color: colors.ink },
  cls: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3 },

  purpose: { fontFamily: fontFamily.sans, color: colors.ink2, fontSize: fontSize.lg, lineHeight: 23, marginBottom: 18 },
  warnBox: { flexDirection: 'row', gap: 10, padding: 12, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.warnLine, backgroundColor: colors.warnSoft, borderRadius: radii.r3, marginBottom: 18 },
  warnTitle: { fontFamily: fontFamily.sans, fontWeight: fontWeight.medium, color: colors.warn },
  warnSub: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, marginTop: 2 },
  sectionLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink3, fontWeight: fontWeight.semibold, marginBottom: 8 },
  chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 18 },
  productChip: { paddingVertical: 4, paddingHorizontal: 10, borderRadius: radii.full, borderWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.accentSoft },
  productChipText: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.accentInk },
  metaRow: { flexDirection: 'row', gap: 16, padding: 14, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, backgroundColor: colors.paper2 },
  metaKey: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.4 },
  metaVal: { fontFamily: fontFamily.mono, fontSize: fontSize.md, color: colors.ink, marginTop: 2 },

  footer: { padding: 14, paddingHorizontal: 20, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.paper2, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' },
  footerLeft: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  footerRight: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' },
  internalBadge: { height: 17, paddingHorizontal: 6, borderRadius: radii.full, backgroundColor: colors.ink, alignItems: 'center', justifyContent: 'center' },
  internalText: { fontFamily: fontFamily.sans, fontSize: 9, fontWeight: fontWeight.semibold, color: colors.paper as any, textTransform: 'uppercase', letterSpacing: 0.4 },
});
